import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { parseTags } from "./tags";

/** Beyond this many, the rest collapse into a single «+N» chip (§5.6). */
const MAX_VISIBLE_TAGS = 6;

interface Props {
  /** Unique prefix for ids (BUG-504/508). */
  idPrefix: string;
  /** Comma-joined tag string — the wire format the order payload already uses. */
  value: string;
  onChange: (next: string) => void;
}

/**
 * Tag chips + a free-text "добавить" input.
 *
 * The chips **wrap**, they do not scroll horizontally: a horizontally-scrolling
 * strip inside a grid column is how the whole dialog ends up with a sideways
 * scrollbar at 1280 (§5.6). Each chip is `max-w-[120px] truncate` with the full
 * text in `title`, so one 80-character tag cannot widen the column either.
 */
export function TagsInput({ idPrefix, value, onChange }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const tags = parseTags(value);
  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = tags.length - visible.length;

  function commitDraft() {
    const next = draft.trim();
    if (!next) return;
    // Ignore an exact duplicate rather than storing it twice — the backend
    // de-duplicates anyway, so keeping it would only misreport the count.
    if (tags.includes(next)) {
      setDraft("");
      return;
    }
    onChange([...tags, next].join(", "));
    setDraft("");
  }

  function removeTag(tag: string) {
    onChange(tags.filter((item) => item !== tag).join(", "));
  }

  return (
    <div className="min-w-0">
      <label
        htmlFor={`${idPrefix}-tags`}
        className="mb-1 block text-xs font-semibold text-gray-700"
      >
        {t("orders.form.tags")}
      </label>
      <div className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-xl border border-gray-200 px-2 py-1 focus-within:border-brand-primary focus-within:ring-1 focus-within:ring-brand-primary 2xl:min-h-[42px] 2xl:py-1.5">
        {visible.map((tag) => (
          <span
            key={tag}
            title={tag}
            className="inline-flex max-w-[120px] items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
          >
            <span className="truncate">{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(tag)}
              aria-label={t("orders.form.removeTag", { tag })}
              className="shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        {overflow > 0 && (
          <span
            title={tags.slice(MAX_VISIBLE_TAGS).join(", ")}
            className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-500"
          >
            +{overflow}
          </span>
        )}
        <input
          id={`${idPrefix}-tags`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitDraft();
            } else if (e.key === "Backspace" && draft === "" && tags.length) {
              onChange(tags.slice(0, -1).join(", "));
            }
          }}
          onBlur={commitDraft}
          placeholder={t("orders.form.tagsAdd")}
          className="min-w-[6rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>
    </div>
  );
}
